import React, { CSSProperties, useContext, useEffect, useState } from 'react';
import styles from './Profile.module.scss';
import Map, { ILatLng } from '../../components/map/Map';
import { getCurrentPosition, mapToGenericResults } from '../../lib/util';
import { AppContext } from '../../context/AppContext';
import axios, { AxiosResponse } from 'axios';
import IGenericResult from '../../interfaces/IGenericResult';
import DirectionsPanel from '../../components/directions-panel/DirectionsPanel';
import html2canvas from 'html2canvas';
import { jsPDF, jsPDFOptions } from 'jspdf';
import { DirectoryListType } from '../../components/directory-list/DirectoryList';
import { IAppContext } from '../../context/DefaultAppState';
import GenericButton from '../../components/generic-button/GenericButton';
import { MapMarkerIcon, ProfileIcon } from '../../components/icons/Icons';
import InfoSection from '../../components/info-section/InfoSection';
import ReportForm from '../../components/report-form/ReportForm';
import BeatLoader from 'react-spinners/BeatLoader';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { pdf } from '@react-pdf/renderer';
import { GeneratePdf } from './GeneratePdf';

const override: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  margin: 'auto',
  width: '100%',
  marginTop: 30
};

const ProfilePage = (props: any) => {
  const appContext = useContext(AppContext);
  const { planCode, directory, recordId } = props.match.params;
  const [primaryResult, setPrimaryResult] = useState<IGenericResult | null>(null);
  const [secondaryResults, setSecondaryResults] = useState<IGenericResult[]>([]);
  const [directions, setDirections] = useState<google.maps.DirectionsResult>();
  const [formattedAddress, setFormattedAddress] = useState('');
  const [showDirectionsPanel, setShowDirectionsPanel] = useState(false);
  const [showEmailField, setShowEmailField] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [miles, setMiles] = useState('');
  const [profileIsDoctor, setProfileIsDoctor] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [color] = useState('#1DAA51');
  const [googleMapUrl, setGoogleMapUrl] = useState('');
  const [blobString, setBlobString] = useState('');
  const [blobPdf, setBlobPdf] = useState<Blob>();

  // This adjusts the height of the PDF according to how many rows there are (typically 3 per row).
  const heightModifier = secondaryResults.length ? Math.round(secondaryResults.length / 3) * 40 : 0;

  const getProfileInformation = (
    props: any,
    setPrimaryResult: React.Dispatch<React.SetStateAction<IGenericResult | null>>,
    setSecondaryResults: React.Dispatch<React.SetStateAction<IGenericResult[]>>,
    setMiles: any,
    setFormattedAddress: any,
    setProfileIsDoctor: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setIsLoading(true);
    (async () => {
      try {
        let directoryType = 0;
        switch (props.match.params.directory) {
          case DirectoryListType.doctor:
            directoryType = 1;
            break;
          case DirectoryListType.facility:
            directoryType = 2;
            break;
          case DirectoryListType.pharmacy:
            directoryType = 3;
            break;
          case DirectoryListType.dentist:
            directoryType = 4;
            break;
          default:
            directoryType = 0;
            break;
        }

        let url = `/Profile/${props.match.params.planCode}/${props.match.params.recordId}/${directoryType}`;

        if (props.match.params.directory === 'dentist') {
          url = `/plans/${props.match.params.planCode}/providers/${props.match.params.recordId}`;
        }

        const { data } = await axios.get(url);
        const primaryResult: IGenericResult = mapToGenericResults([data.primaryResult])[0];
        const secondaryResults: IGenericResult[] = mapToGenericResults(data.secondaryResults);

        setPrimaryResult(primaryResult);
        setSecondaryResults(secondaryResults);
        setProfileIsDoctor(directoryType === 1 ? true : false);

        getDistanceForProfile(
          primaryResult?.addressLine1 as string,
          primaryResult?.addressLine2 as string,
          primaryResult?.city as string,
          primaryResult?.state as string,
          primaryResult?.zipCode as string,
          setMiles,
          setFormattedAddress
        );
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
    })();
  };

  useEffect(() => {
    getProfileInformation(props, setPrimaryResult, setSecondaryResults, setMiles, setFormattedAddress, setProfileIsDoctor);
  }, [recordId]);

  useEffect(() => {
    rebuildContext(planCode, directory, appContext);
  }, [planCode, directory, appContext.plansData]);

  // Generate PDF
  const createPdf = async (pdfName: string, heightModifier?: number, packageForEmail?: boolean, email?: string) => {
    const primaryPractice = {
      practice: primaryResult?.practices,
      addressLine1: primaryResult?.addressLine1,
      city: primaryResult?.city,
      state: primaryResult?.state,
      zipCode: primaryResult?.zipCode,
      phone: primaryResult?.phone,
      fax: primaryResult?.fax
    };

    const secondaryPractices = secondaryResults.map((result: IGenericResult) => {
      return {
        practice: result.practices,
        addressLine1: result.addressLine1,
        city: result.city,
        state: result.state,
        zipCode: result.zipCode,
        phone: result.phone,
        fax: result.fax
      };
    });
    const practices = [primaryPractice, ...secondaryPractices];
    const pdfInfo = { ...primaryResult, practicesArray: practices, miles: miles };

    const currentBlob = await createBlob(pdfInfo);
    const currentBlobString = pdf(GeneratePdf(pdfInfo, googleMapUrl)).toString();

    setBlobPdf(currentBlob);

    if (!packageForEmail) {
      const url = window.URL.createObjectURL(currentBlob);
      window.open(url);
    } else {
      await readAndEmailPdf(currentBlob);
    }
  };

  const createBlob = async (pdfInfo: any) => {
    const currentBlob = await pdf(GeneratePdf(pdfInfo, googleMapUrl)).toBlob();
    return currentBlob;
  }

  const readAndEmailPdf = async (currentBlob: any) => {
    const reader = new FileReader();

      reader.onabort = () => console.error('file reading was aborted');
      reader.onerror = () => console.error('file reading has failed');

      reader.onload = () => {
        const newFile = reader.result as string;

        axios
          .post(`/SendEmail`, {
            planTitle: window.location.host.split('.')[0],
            planBase: 'simpra.com',
            fileAttachment: newFile.split(',')[1],
            fileName: primaryResult?.firstName as string,
            userEmail: userEmail
          })
          .then((res) => {
            console.log(res);
          })
          .catch((err) => {
            console.error(err);
          });
      };

      reader
        .readAsDataURL(currentBlob);
    }

  useEffect(() => {
    if (sessionStorage.getItem('markers')) {
      const stringMarkers = sessionStorage.getItem('markers');
      const stringMarkersJson = stringMarkers ? JSON.parse(stringMarkers) : [];
      const finalMarkers = stringMarkersJson
        .map((y: any, i: number) => {
          return `&markers=color:red|label:${i + 1}|${y}`;
        })
        .join('');
      setGoogleMapUrl(`https://maps.googleapis.com/maps/api/staticmap?size=800x800${finalMarkers}&key=${process.env.REACT_APP_MAPS_API_KEY}`);
    }
  }, []);

  console.log('Google Map URL: ', googleMapUrl);
  console.log('miles: ', miles);
  const isLoadingMaps = miles === '';
  console.log('isLoadingMaps: ', isLoadingMaps);

  /* const downloadPdf = (pdfName: string, heightModifier?: number, packageForEmail?: boolean, email?: string) => {
    // TOOD: Hutch think we only need this function to send the pdf
    axios
      .post(`/SendEmail`, {
        planTitle: window.location.host.split('.')[0],
        planBase: 'simpra.com',
        //fileAttachment: ,
        // fileAttachment: btoa(pdf.output()), // TODO: Need your help here, please change it with createPDF() function
        fileName: pdfName,
        userEmail: email
      })
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.error(err);
      });
  }; */

  return (
    <div id="profile-container">
      {/* <div className="header-container-mini">
        <HeaderControls />
        <div>
          <DirectoryButtonArea />
        </div>
        <div className="select-mini-bg">
          <SearchBar />
        </div>
      </div> */}

      <div className={styles.headerContainer} id="headerContainer">
        <div className={styles.headerContainerButtons}>
          <h2>{primaryResult?.name || primaryResult?.firstName}</h2>
          <div className="white-buttons-container no-print" id="header-container-buttons">
            <GenericButton buttonText={'Download'} handleClick={() => createPdf(primaryResult?.firstName as string, heightModifier)} isDisabled={isLoadingMaps}>
              <VerticalAlignBottomIcon fontSize="inherit" />
            </GenericButton>
            <GenericButton buttonText={'Email'} handleClick={() => setShowEmailField(!showEmailField)} isDisabled={isLoadingMaps}>
              {/* <EmailIcon /> */}
              <EmailOutlinedIcon fontSize="inherit" />
            </GenericButton>
            <GenericButton buttonText={'Print'} handleClick={() => window.print()} isDisabled={isLoadingMaps}>
              {/* <PrintIcon /> */}
              <LocalPrintshopOutlinedIcon fontSize="inherit" />
            </GenericButton>
          </div>
        </div>
        {showDirectionsPanel ? (
          <DirectionsPanel
            formattedAddress={formattedAddress}
            setShowDirectionsPanel={setShowDirectionsPanel}
            setValidationMessage={setValidationMessage}
            setDirections={setDirections}
            getDirections={getDirections}
            getDistance={getDistance}
            setMiles={setMiles}
            toAddress={`${primaryResult?.addressLine1}, ${primaryResult?.addressLine2 ? primaryResult?.addressLine2 + ', ' : ''}${primaryResult?.city}, ${primaryResult?.state} ${
              primaryResult?.zipCode
            }`}
            formattedToAddress={`${primaryResult?.addressLine1} ${primaryResult?.addressLine2 ? primaryResult?.addressLine2 + ' ' : ''}${primaryResult?.city} ${primaryResult?.state} ${
              primaryResult?.zipCode
            }`
              .split(' ')
              .join('+')}
          />
        ) : null}
        {showEmailField ? (
          <div className={styles.emailFieldContainer}>
            <hr />
            <h4>Email Result</h4>
            <p>Please Enter an Email Address</p>
            <input type="text" placeholder={'example: john.doe@example.com'} onChange={(e) => setUserEmail(e.target.value)} />
            <button
              className={styles.profileButton}
              onClick={() =>
                validateEmail(userEmail, setValidationMessage, () => {
                  createPdf(primaryResult?.firstName as string, heightModifier, true, userEmail);
                  setShowEmailField(!showEmailField);
                })
              }
            >
              Send Email
            </button>
          </div>
        ) : null}
        {/* <p className={styles.error}>{validationMessage}</p>
            <hr /> */}
      </div>

      {isLoading ? (
        <BeatLoader color={color} cssOverride={override} size={50} aria-label="Loading Spinner" data-testid="loader" />
      ) : (
        <div className={styles.profileContainer} id="profileContainer">
          <div className={`${styles.infoContainer}`}>
            <div className={styles.infoSection}>
              <InfoSection key={primaryResult?.firstName} styles={styles} primaryResult={primaryResult} secondaryResults={secondaryResults} resultIsDoctor={profileIsDoctor} />
              <div className="only-on-print" style={{ fontSize: '14px' }}>
                {miles && (
                  <>
                    <span className={styles.locationPrint}>
                      <b>Distance:</b> {miles} mi
                    </span>
                    <span className={styles.location} id="location">
                      <LocationOnOutlinedIcon style={{ color: '#1DAA51', marginRight: 10 }} /> {miles} mi
                    </span>
                  </>
                )}
              </div>
              <div className="no-print" id="getDirections">
                <GenericButton
                  buttonText={'Get Directions'}
                  handleClick={() => {
                    setShowDirectionsPanel(!showDirectionsPanel);
                    getDistanceForProfile(
                      primaryResult?.addressLine1 as string,
                      primaryResult?.addressLine2 as string,
                      primaryResult?.city as string,
                      primaryResult?.state as string,
                      primaryResult?.zipCode as string,
                      setMiles,
                      setFormattedAddress,
                      setDirections
                    );
                  }}
                >
                  <MapMarkerIcon />
                </GenericButton>
              </div>
              <button
                id="reportIncorrectInformation"
                className="button-as-link error no-print"
                onClick={() => {
                  setShowReportForm(!showReportForm);
                }}
              >
                Report Incorrect Information
              </button>
            </div>

            <div className={`d-none-print ${styles.infoSection}`}>
              <div className={styles.innerSection}>
                <div className={styles.sectionHeader}>{primaryResult?.name}</div>

                <div className={styles.sectionBody}>
                  {primaryResult?.locationName == primaryResult?.name ? null : <><b>{primaryResult?.locationName}</b><br /></>}
                  {primaryResult?.addressLine1},
                  <br />
                  {primaryResult?.addressLine2 ? (
                    <span>
                      {primaryResult?.addressLine2 + ','}
                      <br />
                    </span>
                  ) : null}
                  {primaryResult?.city}, {primaryResult?.state} {primaryResult?.zipCode}
                </div>

                {miles && (
                  <>
                    <p className={styles.locationPrint}>Distance: {miles} mi</p>
                    <p className={styles.location} id="location">
                      <LocationOnOutlinedIcon style={{ color: '#1DAA51', marginRight: 10 }} /> {miles} mi
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
          {/* GOOGLE MAP */}

          <div className={styles.mapContainer} id="map-container">
            <Map currentChunk={[primaryResult, ...secondaryResults]} profileDirections={directions} />
          </div>

          {showReportForm ? <ReportForm primaryResult={primaryResult} setShowReportForm={setShowReportForm} /> : null}

          <div className="flex flex-col only-on-print">
            <div className={styles.mapPrint}>
              <img src={googleMapUrl} alt="Google Map" style={{ width: '350px', height: '350px', margin: 'auto' }} />
            </div>

            <div>
              <span style={{ marginTop: '10px', marginBottom: '10px', display: 'block' }}>Locations: </span>
              <div className={styles.additionColumn}>
                <div className={styles.sectionHeader}>{primaryResult?.firstName}</div>
                <div>
                  <b>
                    <span style={{ marginRight: '5px' }}>1.</span>
                    {primaryResult?.locationName == primaryResult?.firstName ? null : primaryResult?.locationName}
                  </b>
                  <br />
                  {primaryResult?.addressLine1},
                  <br />
                  {primaryResult?.addressLine2 ? (
                    <span>
                      {primaryResult?.addressLine2 + ','}
                      <br />
                    </span>
                  ) : null}
                  {primaryResult?.city}, {primaryResult?.state} {primaryResult?.zipCode}
                </div>
                <div>Phone: {primaryResult?.phone}</div>
                <div>Fax: {primaryResult?.fax}</div>
              </div>
            </div>
          </div>
          {secondaryResults.length ? (
            <div className={styles.additionalResultsContainer} id="additionalResultsContainer">
              {secondaryResults.map((sResult, i) => (
                <div key={sResult.recordId} className={styles.additionColumn}>
                  <div style={{ display: 'flex', fontWeight: 'bold' }}>
                    <span className="only-on-print" style={{ marginRight: '5px' }}>
                      {i + 2}.{' '}
                    </span>{' '}
                    {sResult.firstName}
                    <b>{sResult.locationName == sResult.firstName ? null : sResult.locationName}</b>
                  </div>
                  <div className="d-none-print">{sResult?.specialty}</div>
                  <span>
                    <div>{sResult?.addressLine1},</div>
                    <div>{primaryResult?.addressLine2 ? <span>{primaryResult?.addressLine2 + ','}</span> : null}</div>
                    <div>
                      {sResult?.city}, {sResult?.state} {sResult?.zipCode}
                    </div>
                  </span>
                  <div>Phone: {sResult.phone}</div>
                  <div>Fax: {sResult.fax}</div>
                  <div className="d-none-print">
                    <GenericButton
                      buttonText={'Use Address'}
                      handleClick={() =>
                        switchProfile(sResult.recordId, primaryResult as IGenericResult, setPrimaryResult, secondaryResults, setSecondaryResults, setMiles, setFormattedAddress, setDirections)
                      }
                    >
                      <ProfileIcon />
                    </GenericButton>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const switchProfile = (
  recordId: string,
  primaryResult: IGenericResult,
  setPrimaryResult: React.Dispatch<React.SetStateAction<IGenericResult | null>>,
  secondaryResults: IGenericResult[],
  setSecondaryResults: React.Dispatch<React.SetStateAction<IGenericResult[]>>,
  setMiles: React.Dispatch<React.SetStateAction<string>>,
  setFormattedAddress: React.Dispatch<React.SetStateAction<string>>,
  setDirections: React.Dispatch<React.SetStateAction<google.maps.DirectionsResult | undefined>>
) => {
  const newPrimary = secondaryResults.find((s) => s.recordId === recordId);
  const newSecondaries: IGenericResult[] = secondaryResults.filter((s) => s.recordId !== recordId);
  newSecondaries.unshift(primaryResult);
  setPrimaryResult(newPrimary as IGenericResult);
  setSecondaryResults(newSecondaries as IGenericResult[]);
  setDirections(undefined);
  getDistanceForProfile(primaryResult?.addressLine1 as string, primaryResult?.addressLine2 as string, primaryResult?.city, primaryResult?.state, primaryResult?.zipCode, setMiles, setFormattedAddress);
};

// TODO: Duplicated From Profile Card. Centralize Functions.

const getDistanceForProfile = (
  address1: string,
  address2: string,
  city: string,
  state: string,
  zipCode: string,
  setMiles: React.Dispatch<React.SetStateAction<string>>,
  setFormattedAddress: React.Dispatch<React.SetStateAction<string>>,
  setDirections?: React.Dispatch<React.SetStateAction<google.maps.DirectionsResult | undefined>>
) => {
  (async () => {
    try {
      let currentLocation = { lat: 0, lng: 0 };
      const res: any = await getCurrentPosition();
      const { latitude, longitude } = res.coords;
      currentLocation.lat = latitude;
      currentLocation.lng = longitude;
      let distance: string = 'N/A';
      if (process.env.REACT_APP_MAPS_API_KEY) {
        const formattedAddress = `${address1} ${address2 ? address2 + ' ' : ''}${city} ${state} ${zipCode}`.split(' ').join('+');
        setFormattedAddress(formattedAddress);
        const geocode = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${formattedAddress}&key=${process.env.REACT_APP_MAPS_API_KEY}`);
        const locationToMark: { lat: number; lng: number } = geocode.data.results[0].geometry.location;
        if (setDirections) {
          getDirections(currentLocation, locationToMark, setDirections);
        }
        distance = getDistance(currentLocation, locationToMark);
      }
      setMiles(distance);
    } catch (err) {
      console.error(err);
    }
  })();
};

const rad = (x: number) => {
  return (x * Math.PI) / 180;
};

const getMiles = (meters: number) => {
  return (meters * 0.000621371192).toFixed(2);
};

const getDistance = (p1: ILatLng, p2: ILatLng): string => {
  var R = 6378137; // Earth’s mean radius in meter
  var dLat = rad(p2.lat - p1.lat);
  var dLong = rad(p2.lng - p1.lng);
  var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(rad(p1.lat)) * Math.cos(rad(p2.lat)) * Math.sin(dLong / 2) * Math.sin(dLong / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  var d = R * c;
  return getMiles(d); // returns the distance in meter
};

const getDirections = (origin: any, destination: any, setDirections: React.Dispatch<React.SetStateAction<google.maps.DirectionsResult | undefined>>) => {
  const DirectionsService = new google.maps.DirectionsService();

  DirectionsService.route(
    {
      origin: new google.maps.LatLng(origin.lat, origin.lng),
      destination: new google.maps.LatLng(destination.lat, destination.lng),
      travelMode: google.maps.TravelMode.DRIVING
    },
    (result, status) => {
      if (result && status === google.maps.DirectionsStatus.OK) {
        setDirections(result);
      } else {
        console.error(`error fetching directions ${result}`);
        return {
          geocoded_waypoints: [],
          routes: []
        };
      }
    }
  );
};

const validateEmail = (email: string, setValidationMessage: any, callback: () => void) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  let isValid = regex.test(email);

  if (isValid) {
    setValidationMessage('');
    callback();
  } else {
    setValidationMessage('Email is not valid. Please check that the email address entered is formatted correctly.');
  }
};

const rebuildContext = (planCode: string | null, directory: string | null, appContext: IAppContext) => {
  if (planCode) {
    const currentPlan = appContext.plansData.find((p) => p.contractId === planCode);

    if (currentPlan) {
      appContext.updatePlanCode(planCode);
      appContext.updatePlan(currentPlan);
      appContext.updatePlanName(currentPlan.planName);
      appContext.updateDisclaimerUrl(currentPlan.ipdDisclaimerLink);
      appContext.updatePlanState(currentPlan.state);
    }
  }

  if (directory) {
    switch (directory) {
      case DirectoryListType.doctor:
        appContext.updateListType(DirectoryListType.doctor);
        appContext.updateSelected(true);
        break;
      case DirectoryListType.facility:
        appContext.updateListType(DirectoryListType.facility);
        appContext.updateSelected(true);
        break;
      case DirectoryListType.pharmacy:
        appContext.updateListType(DirectoryListType.pharmacy);
        appContext.updateSelected(true);
        break;
      case DirectoryListType.dentist:
        appContext.updateListType(DirectoryListType.dentist);
        appContext.updateSelected(true);
        break;
      case DirectoryListType.all:
        appContext.updateListType(DirectoryListType.all);
        appContext.updateSelected(true);
        break;
      default:
        appContext.updateListType(DirectoryListType.none);
        appContext.updateSelected(false);
        break;
    }
  }
};

export default ProfilePage;
